import React from 'react';
import { Helmet } from 'react-helmet';

const OtherCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-orange-100 to-yellow-100">
      <Helmet>
        <title>Other Calculators | Miscellaneous Calculation Tools</title>
        <meta name="description" content="Collection of 150+ miscellaneous calculators for various purposes including general math, business, conversions, and special-purpose calculations." />
        <meta name="keywords" content="other calculators, miscellaneous calculators, general math calculators, business calculators, conversion tools" />
        <meta property="og:title" content="Other Calculators | Miscellaneous Calculation Tools" />
        <meta property="og:description" content="Collection of 150+ miscellaneous calculators for various purposes including general math, business, conversions, and special-purpose calculations." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Other Calculators | Miscellaneous Calculation Tools" />
        <meta name="twitter:description" content="Collection of 150+ miscellaneous calculators for various purposes including general math, business, conversions, and special-purpose calculations." />
        <link rel="canonical" href="https://yourdomain.com/other-calculators" />
      </Helmet>

      {/* WhatsApp Support Button */}
      <div className="fixed bottom-6 right-6">
        <a 
          href="https://api.whatsapp.com/send?phone=YOUR_PHONE_NUMBER&text=I%20need%20help%20with%20the%20Other%20Calculators" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-4 rounded-full shadow-lg transition-all transform hover:scale-105"
        >
          <span className="text-2xl">💬</span>
          <span className="sr-only">Contact us on WhatsApp</span>
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-yellow-600 mb-4">🧮</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Other Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">150+ calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Explore our collection of miscellaneous calculators that don't fit into specialized categories. These tools cover a wide range of general math, business, conversion, and special-purpose calculations to help with various everyday needs.
            </p>
            <button className="read-more inline-block bg-yellow-600 text-white py-2 px-4 rounded-md hover:bg-yellow-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            General Mathematics <span className="emoji ml-2">🔢</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/percentage" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Percentage Calculator</h3>
                <p className="text-gray-600">Calculate percentages and percentage changes</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/fraction" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Fraction Calculator</h3>
                <p className="text-gray-600">Perform operations with fractions</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/decimal" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Decimal Calculator</h3>
                <p className="text-gray-600">Convert between decimals and fractions</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/square-root" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Square Root Calculator</h3>
                <p className="text-gray-600">Calculate square roots of numbers</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/exponent" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Exponent Calculator</h3>
                <p className="text-gray-600">Calculate powers and exponents</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/logarithm" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Logarithm Calculator</h3>
                <p className="text-gray-600">Calculate logarithms with any base</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Business & Finance <span className="emoji ml-2">💰</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/roi" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">ROI Calculator</h3>
                <p className="text-gray-600">Calculate Return on Investment</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/break-even" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Break-Even Calculator</h3>
                <p className="text-gray-600">Determine break-even points</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/markup" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Markup Calculator</h3>
                <p className="text-gray-600">Calculate pricing and markup percentages</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/currency-conversion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Currency Conversion Calculator</h3>
                <p className="text-gray-600">Convert between different currencies</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/profit-margin" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Profit Margin Calculator</h3>
                <p className="text-gray-600">Calculate your profit margins</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/cagr" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">CAGR Calculator</h3>
                <p className="text-gray-600">Calculate Compound Annual Growth Rate</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Conversion Tools <span className="emoji ml-2">🔄</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/unit-conversion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Unit Conversion Calculator</h3>
                <p className="text-gray-600">Convert between different measurement units</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/temperature-conversion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Temperature Conversion Calculator</h3>
                <p className="text-gray-600">Convert between Celsius, Fahrenheit, and Kelvin</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/length-conversion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Length Conversion Calculator</h3>
                <p className="text-gray-600">Convert between length units</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/weight-conversion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Weight Conversion Calculator</h3>
                <p className="text-gray-600">Convert between weight units</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/volume-conversion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Volume Conversion Calculator</h3>
                <p className="text-gray-600">Convert between volume units</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/area-conversion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Area Conversion Calculator</h3>
                <p className="text-gray-600">Convert between area units</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Miscellaneous Tools <span className="emoji ml-2">🛠️</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/random-number" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Random Number Generator</h3>
                <p className="text-gray-600">Generate random numbers within a range</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/date-difference" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Date Difference Calculator</h3>
                <p className="text-gray-600">Calculate days between two dates</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/age-calculator" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Age Calculator</h3>
                <p className="text-gray-600">Calculate age from birthdate</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/timer" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Timer Calculator</h3>
                <p className="text-gray-600">Set and manage timers</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/countdown" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Countdown Calculator</h3>
                <p className="text-gray-600">Create custom countdowns</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/password-strength" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Password Strength Calculator</h3>
                <p className="text-gray-600">Analyze password strength</p>
              </a>
            </div>
          </div>
        </div>
        <div className="calculator-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Business & Finance <span className="emoji ml-2">💰</span>
  </h2>
  <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/roi" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">ROI Calculator</h3>
        <p className="text-gray-600">Calculate Return on Investment</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/break-even" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Break-Even Calculator</h3>
        <p className="text-gray-600">Determine break-even points</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/markup" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Markup Calculator</h3>
        <p className="text-gray-600">Calculate pricing and markup percentages</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/currency-conversion" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Currency Conversion Calculator</h3>
        <p className="text-gray-600">Convert between different currencies</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/profit-margin" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Profit Margin Calculator</h3>
        <p className="text-gray-600">Calculate your profit margins</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/cagr" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">CAGR Calculator</h3>
        <p className="text-gray-600">Calculate Compound Annual Growth Rate</p>
      </a>
    </div>

    {/* Additional calculators */}
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/16-10-aspect-ratio" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">16:10 Aspect Ratio Calculator</h3>
        <p className="text-gray-600">Calculate the 16:10 aspect ratio</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/16-9-aspect-ratio" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">16:9 Aspect Ratio Calculator</h3>
        <p className="text-gray-600">Calculate the 16:9 aspect ratio</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/4-3-aspect-ratio" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">4:3 Aspect Ratio Calculator</h3>
        <p className="text-gray-600">Calculate the 4:3 aspect ratio</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/carat-weight" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Carat Weight Calculator</h3>
        <p className="text-gray-600">Calculate diamond carat weight</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/css-aspect-ratio" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">CSS Aspect Ratio Calculator</h3>
        <p className="text-gray-600">Calculate aspect ratios for CSS</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/days-until-fall" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Days Until Fall Calculator</h3>
        <p className="text-gray-600">Calculate days until fall</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/days-until-valentines-day" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Days Until Valentine's Day Calculator</h3>
        <p className="text-gray-600">Calculate days until Valentine's Day</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/diamond-carat" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Diamond Carat Calculator</h3>
        <p className="text-gray-600">Calculate diamond carat size</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/even-parity-bit" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Even Parity Bit Calculator</h3>
        <p className="text-gray-600">Calculate even parity bits</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/mm-to-carat" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">mm to Carat Conversion Calculator</h3>
        <p className="text-gray-600">Convert mm to carat</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/new-year-countdown" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">New Year Countdown Calculator</h3>
        <p className="text-gray-600">Countdown to the New Year</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/odd-parity-bit" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Odd Parity Bit Calculator</h3>
        <p className="text-gray-600">Calculate odd parity bits</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/parity" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Parity Calculator</h3>
        <p className="text-gray-600">Calculate parity</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/pixel-aspect-ratio" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Pixel Aspect Ratio Calculator</h3>
        <p className="text-gray-600">Calculate pixel aspect ratios</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/portrait-aspect-ratio" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Portrait Aspect Ratio Calculator</h3>
        <p className="text-gray-600">Calculate portrait aspect ratio</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/screen-ratio" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Screen Ratio Calculator</h3>
        <p className="text-gray-600">Calculate screen aspect ratios</p>
      </a>
    </div>

    {/* Additional requested calculators */}
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/age-on-other-planets" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Age on Other Planets Calculator</h3>
        <p className="text-gray-600">Calculate your age on other planets</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/angle-of-impact" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Angle of Impact Calculator</h3>
        <p className="text-gray-600">Calculate the angle of impact</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/aquarium" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Aquarium Calculator</h3>
        <p className="text-gray-600">Calculate aquarium volume and needs</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/aquarium-glass-thickness" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Aquarium Glass Thickness Calculator</h3>
        <p className="text-gray-600">Calculate appropriate glass thickness for an aquarium</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/recruitment-process-duration" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Recruitment Process Duration Calculator</h3>
        <p className="text-gray-600">Estimate the duration of a recruitment process</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/day-of-the-week" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Day of the Week Calculator</h3>
        <p className="text-gray-600">Determine the day of the week for a given date</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/diamond-weight" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Diamond Weight Calculator</h3>
        <p className="text-gray-600">Calculate diamond weight based on dimensions</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/encounter-5e" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Encounter Calculator 5e</h3>
        <p className="text-gray-600">Calculate Dungeons & Dragons encounters</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/engagement-rate" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Engagement Rate Calculator</h3>
        <p className="text-gray-600">Calculate social media engagement rates</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/flag-usa" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Flag Calculator | USA</h3>
        <p className="text-gray-600">Determine the proportions of the USA flag</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/gender-pay-gap" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Gender Pay Gap Calculator</h3>
        <p className="text-gray-600">Calculate gender pay gap percentages</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/hiring-expert-vs-fresher" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Cost of Hiring an Expert vs. Hiring a Fresher Calculator</h3>
        <p className="text-gray-600">Compare the cost of hiring an expert versus a fresher</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/hp-5e-dnd" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">HP Calculator 5e – Dungeons & Dragons</h3>
        <p className="text-gray-600">Calculate HP for Dungeons & Dragons 5e</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/matched-betting" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Matched Betting Calculator</h3>
        <p className="text-gray-600">Calculate matched betting profits</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/nanowrimo" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">NaNoWriMo Calculator</h3>
        <p className="text-gray-600">Track your NaNoWriMo progress</p>
      </a>
    </div>
  </div>
</div>
<div className="calculator-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Business & Finance <span className="emoji ml-2">💰</span>
  </h2>
  <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/roi" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">ROI Calculator</h3>
        <p className="text-gray-600">Calculate Return on Investment</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/break-even" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Break-Even Calculator</h3>
        <p className="text-gray-600">Determine break-even points</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/markup" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Markup Calculator</h3>
        <p className="text-gray-600">Calculate pricing and markup percentages</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/currency-conversion" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Currency Conversion Calculator</h3>
        <p className="text-gray-600">Convert between different currencies</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/profit-margin" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Profit Margin Calculator</h3>
        <p className="text-gray-600">Calculate your profit margins</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/cagr" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">CAGR Calculator</h3>
        <p className="text-gray-600">Calculate Compound Annual Growth Rate</p>
      </a>
    </div>

    {/* Additional calculators */}
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/oxygen-tank-duration" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Oxygen Tank Duration Calculator</h3>
        <p className="text-gray-600">Calculate the duration of an oxygen tank</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/pay-gap" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Pay Gap Calculator</h3>
        <p className="text-gray-600">Calculate pay gap percentages</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/pert" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">PERT Calculator</h3>
        <p className="text-gray-600">Calculate PERT (Program Evaluation and Review Technique)</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/pizza-tip" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Pizza Tip Calculator</h3>
        <p className="text-gray-600">Calculate tip for pizza orders</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/point-buy-5e" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Point Buy Calculator 5e</h3>
        <p className="text-gray-600">Calculate point buy for Dungeons & Dragons 5e</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/poker-ev" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Poker EV Calculator</h3>
        <p className="text-gray-600">Calculate poker expected value (EV)</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/poker-odds" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Poker Odds Calculator</h3>
        <p className="text-gray-600">Calculate poker odds</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/rounds-per-minute" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Rounds per Minute Calculator</h3>
        <p className="text-gray-600">Calculate rounds per minute (RPM)</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/salt-dough" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Salt Dough Calculator</h3>
        <p className="text-gray-600">Calculate salt dough recipe ingredients</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/sandbox" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Sandbox Calculator</h3>
        <p className="text-gray-600">Calculate sandbox area and volume</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/dating-theory" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Dating Theory Calculator</h3>
        <p className="text-gray-600">Calculate compatibility based on dating theory</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/streaming-royalties" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Streaming Services' Royalties Calculator</h3>
        <p className="text-gray-600">Calculate royalties from streaming services</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/tip" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Tip Calculator</h3>
        <p className="text-gray-600">Calculate tips based on the bill</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/tip-from-net-price" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Tip from Net Price Calculator</h3>
        <p className="text-gray-600">Calculate tip based on net price</p>
      </a>
    </div>
    <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/travel-expenses" className="calculator-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Travel Expenses Calculator</h3>
        <p className="text-gray-600">Calculate travel expenses for trips</p>
      </a>
    </div>
  </div>
</div>


      </div>
    </div>
  );
};

export default OtherCalculators;